import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import SearchProductCard from "./SearchProductCard";
import {
  PublicProductList,
  PublicProductListResponse,
} from "interfaces/IProduct";
import {
  fetchPublicCategories,
  fetchPublicProducts,
  fetchPublicSizes,
} from "api/auth";
import { useBreakpoints } from "hooks/useBreakPoints";
import ProductModal from "../products/ProductModal";
import CustomNavbarClients from "components/clients/components/navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import "./SearchProducts.scss";
import { PublicCategory } from "interfaces/ICategory";
import { Size } from "interfaces/ISize";
import { Slider } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import TriangleIcon from "components/icons/TriangleIcon";
import Footer from "../Footer";
import CustomToast from "components/general/CustomToast";
import LoadingScreen from "components/general/LoadingScreen";

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<PublicProductList[]>([]);
  const { isMobile, isTablet } = useBreakpoints();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicProductList>();
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [allSizes, setAllSizes] = useState<Size[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [sortBy, setSortBy] = useState("featured");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const searchQuery = searchParams.get("q") || "";
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [inputPriceRange, setInputPriceRange] = useState<[number, number]>([
    0, 5000,
  ]);
  const [appliedPriceRange, setAppliedPriceRange] = useState<[number, number]>([
    0, 5000,
  ]);
  const [showToast, setShowToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "", show: false, success: true });
  const [isFilteringSizes, setIsFilteringSizes] = useState(false);

  /**
   * Handles changes to the price range slider.
   * Ensures that the minimum value is always less than or equal to the maximum value.
   * Updates both the price range state and the input price range state simultaneously.
   *
   * @param {any} event - The event object (unused but required by MUI Slider)
   * @param {number | number[]} newValue - The new slider value(s)
   */
  const handlePriceRangeChange = (event: any, newValue: number | number[]) => {
    const [min, max] = newValue as [number, number];
    setPriceRange([min, Math.max(min, max)]);
    setInputPriceRange([min, Math.max(min, max)]);
  };

  /**
   * Handles manual input changes for price range fields.
   * Validates input to ensure only numeric values are accepted.
   * Ensures logical constraints are maintained (min ≤ max, max ≥ min).
   *
   * @param {number} index - 0 for minimum price field, 1 for maximum price field
   * @param {string} value - The string value entered in the input field
   */
  const handleInputChange = (index: number, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    const newValue = numericValue === "" ? 0 : parseInt(numericValue);
    const newInputRange = [...inputPriceRange] as [number, number];

    if (index === 0) {
      // Min price
      newInputRange[0] = Math.min(newValue, inputPriceRange[1]);
    } else {
      // Max price
      newInputRange[1] = Math.max(newValue, inputPriceRange[0]);
    }

    setInputPriceRange(newInputRange);
    setPriceRange(newInputRange);
  };

  /**
   * Applies the current input price range to the filter.
   * This triggers a re-fetch of products with the new price range filter.
   */
  const handleApplyPrice = () => {
    setAppliedPriceRange(inputPriceRange);
    setPriceRange(inputPriceRange);
  };

  /**
   * Effect hook to load all available sizes when the component mounts.
   * Fetches sizes from the API and updates the local state.
   *
   * @effect
   * @dependencies [] - Runs only once when the component mounts
   */
  useEffect(() => {
    const loadSizes = async () => {
      try {
        const response = await fetchPublicSizes();
        setSizes(response);
        setAllSizes(response); // Guardar todas las tallas para filtrado
      } catch (error) {
        console.error("Error fetching sizes:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadSizes();
  }, []);

  /**
   * Effect hook to load and filter products based on search criteria and filters.
   * Fetches products from the API with query parameters built from the current filter states.
   *
   * @effect
   * @dependencies [searchQuery, sortBy, sortOrder, selectedCategories, selectedSizes, appliedPriceRange]
   *               - Re-runs when any of these filter states change
   */
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const params: any = {
          name: searchQuery,
          sort_by: sortBy === "price" ? "price" : "created_at",
          order: sortBy === "price" ? sortOrder : "desc",
          categories:
            selectedCategories.length > 0
              ? selectedCategories.join(",")
              : undefined,
          sizes: selectedSizes.length > 0 ? selectedSizes.join(",") : undefined,
        };

        if (appliedPriceRange[0] > 0 || appliedPriceRange[1] < 5000) {
          params.min_price = appliedPriceRange[0].toString();
          params.max_price = appliedPriceRange[1].toString();
        }

        const response: PublicProductListResponse = await fetchPublicProducts(
          params
        );
        setProducts(response.products);
      } catch (error) {
        console.error("Error loading products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [
    searchQuery,
    sortBy,
    sortOrder,
    selectedCategories,
    selectedSizes,
    appliedPriceRange,
  ]);

  /**
   * Effect hook to load all available categories when the component mounts.
   * Fetches categories from the API and updates the local state.
   *
   * @effect
   * @dependencies [] - Runs only once when the component mounts
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchPublicCategories();
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        // Solo marcar como no loading si las tallas también se han cargado
        if (!isInitialLoading) {
          setIsInitialLoading(false);
        }
      }
    };

    loadCategories();
  }, [isInitialLoading]);

  // Efecto para filtrar tallas cuando cambian las categorías seleccionadas
  useEffect(() => {
    if (allSizes.length > 0) {
      // Usar una función async interna
      const updateSizes = async () => {
        await filterSizesByCategories(selectedCategories);
      };
      updateSizes();
    }
  }, [selectedCategories, allSizes]);

  /**
   * Effect hook to manage body scroll behavior when filter menu is shown/hidden.
   * Prevents body scrolling when filters are open by adding/removing event listeners
   * and CSS classes. Restores scroll position when filters are closed.
   *
   * @effect
   * @dependencies [showFilters] - Re-runs when the filter visibility state changes
   * @cleanup Restores body scroll behavior and removes event listeners
   */
  useEffect(() => {
    const preventPropagation = (e: Event) => {
      const target = e.target as Element;
      const filterMenu = document.querySelector(".filter-menu");
      const filterContent = document.querySelector(".filter-content");

      if (
        (filterMenu && !filterMenu.contains(target)) ||
        (filterContent &&
          !filterContent.contains(target) &&
          target !== filterContent)
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (showFilters) {
      const scrollY = window.scrollY;
      document.body.classList.add("no-scroll");
      document.body.style.top = `-${scrollY}px`;

      document.addEventListener("touchmove", preventPropagation, {
        passive: false,
        capture: true,
      });
      document.addEventListener("wheel", preventPropagation, {
        passive: false,
        capture: true,
      });
    } else {
      const scrollY = parseInt(document.body.style.top || "0", 10) * -1;
      document.body.classList.remove("no-scroll");
      document.body.style.top = "";
      window.scrollTo(0, scrollY);

      document.removeEventListener("touchmove", preventPropagation, {
        capture: true,
      });
      document.removeEventListener("wheel", preventPropagation, {
        capture: true,
      });
    }

    return () => {
      document.body.classList.remove("no-scroll");
      document.body.style.top = "";
      document.removeEventListener("touchmove", preventPropagation, {
        capture: true,
      });
      document.removeEventListener("wheel", preventPropagation, {
        capture: true,
      });
    };
  }, [showFilters]);

  /**
   * Effect hook to calculate and set the scrollbar width as a CSS variable.
   * This ensures proper layout adjustments when the scrollbar is present or absent.
   *
   * @effect
   * @dependencies [] - Runs only once when the component mounts
   */
  useEffect(() => {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${scrollbarWidth}px`
    );
  }, []);

  /**
   * Effect hook to initialize selected categories from URL parameters
   * Runs when the component mounts or when searchParams changes
   */
  useEffect(() => {
    const categoriesParam = searchParams.get("categories");
    if (categoriesParam) {
      const categoryIds = categoriesParam
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      // Solo actualizar si es diferente para evitar loops
      setSelectedCategories((prev) => {
        if (
          JSON.stringify(prev.sort()) !== JSON.stringify(categoryIds.sort())
        ) {
          // Filtrar tallas según las categorías iniciales
          filterSizesByCategories(categoryIds);
          return categoryIds;
        }
        return prev;
      });
    } else if (selectedCategories.length > 0) {
      // Solo limpiar si hay categorías seleccionadas
      setSelectedCategories([]);
      // Mostrar todas las tallas cuando no hay categorías
      setSizes(allSizes);
    }

    // Initialize price range from URL parameters
    const minPriceParam = searchParams.get("minPrice");
    const maxPriceParam = searchParams.get("maxPrice");
    
    if (minPriceParam || maxPriceParam) {
      const minPrice = minPriceParam ? parseInt(minPriceParam) : 0;
      const maxPrice = maxPriceParam ? parseInt(maxPriceParam) : 5000;
      
      setPriceRange([minPrice, maxPrice]);
      setInputPriceRange([minPrice, maxPrice]);
      setAppliedPriceRange([minPrice, maxPrice]);
    }

    // Initialize sizes from URL parameters
    const sizesParam = searchParams.get("sizes");
    if (sizesParam) {
      const sizeIds = sizesParam
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      setSelectedSizes((prev) => {
        if (
          JSON.stringify(prev.sort()) !== JSON.stringify(sizeIds.sort())
        ) {
          return sizeIds;
        }
        return prev;
      });
    } else if (selectedSizes.length > 0) {
      setSelectedSizes([]);
    }
  }, [searchParams]); // Elimina selectedCategories de las dependencias

  /**
   * Toggles a category in the selected categories filter.
   * If the category is already selected, it removes it; otherwise, it adds it.
   *
   * @param {number} categoryId - The ID of the category to toggle
   */
  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) => {
      const newCategories = prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId];
      
      // Si no hay categorías seleccionadas, limpiar también las tallas seleccionadas
      if (newCategories.length === 0) {
        setSelectedSizes([]);
      }
      
      // Filtrar tallas según las categorías seleccionadas
      filterSizesByCategories(newCategories);
      
      return newCategories;
    });
  };

  // Función para filtrar tallas según las categorías seleccionadas
  const filterSizesByCategories = async (categoryIds: number[]) => {
    try {
      setIsFilteringSizes(true);
      
      if (categoryIds.length === 0) {
        // Si no hay categorías seleccionadas, mostrar todas las tallas
        setSizes(allSizes);
        return;
      }

      // Obtener productos de las categorías seleccionadas
      const params: any = {
        categories: categoryIds.join(","),
        limit: 1000, // Obtener muchos productos para tener todas las tallas
      };

      const response = await fetchPublicProducts(params);
      const products = response.products;

      // Extraer todas las tallas únicas de los productos
      const availableSizeNames = new Set<string>();
      products.forEach(product => {
        product.attributes.forEach(attribute => {
          attribute.sizes.forEach(size => {
            availableSizeNames.add(size.size);
          });
        });
      });

      // Filtrar las tallas disponibles por nombre
      const filteredSizes = allSizes.filter(size => availableSizeNames.has(size.description));
      setSizes(filteredSizes);

      // Remover tallas seleccionadas que ya no están disponibles
      setSelectedSizes(prev => prev.filter(sizeId => {
        const size = allSizes.find(s => s.id === sizeId);
        return size && availableSizeNames.has(size.description);
      }));
    } catch (error) {
      console.error("Error filtering sizes by categories:", error);
      // En caso de error, mostrar todas las tallas
      setSizes(allSizes);
    } finally {
      setIsFilteringSizes(false);
    }
  };

  /**
   * Handles click on a product item.
   * Sets the selected product and opens the product detail modal.
   *
   * @param {PublicProductList} product - The product that was clicked
   */
  const handleProductClick = (product: PublicProductList) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  /**
   * Toggles a size in the selected sizes filter.
   * If the size is already selected, it removes it; otherwise, it adds it.
   *
   * @param {number} sizeId - The ID of the size to toggle
   */
  const handleSizeChange = (sizeId: number) => {
    setSelectedSizes((prev) => {
      if (prev.includes(sizeId)) {
        return prev.filter((id) => id !== sizeId);
      }
      return [...prev, sizeId];
    });
  };

  /**
   * Handles sort type selection and toggles sort direction for price.
   * For price sorting, it toggles between ascending and descending order.
   * For other sort types, it just sets the sort type.
   *
   * @param {string} type - The type of sorting to apply ('price', 'featured', etc.)
   */
  const handleSortClick = (type: string) => {
    if (type === "price") {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      setSortBy("price");
    } else {
      setSortBy(type);
    }
  };

  /**
   * handles the toast when the favorite toggle endpoint gives feedback
   */
  const handleToast = (message: string, show: boolean, success: boolean) =>
    setShowToast({ message, show, success });

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />

      {isInitialLoading ? (
        <LoadingScreen loadingText="Cargando productos..." />
      ) : (
        <div className="search-results-container">
        <div className="search-header">
          <div className="title-container">
            {isMobile && <div className="line" />}
            <h1>Resultados</h1>
            {isMobile && <div className="line" />}
          </div>

          <div className="sort-container">
            {(isMobile || isTablet) && (
              <Button
                className="filter-button"
                onClick={() => setShowFilters(true)}
              >
                <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                Filtros
              </Button>
            )}
            {!isMobile && (
              <div
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginRight: "-3rem",
                  marginBottom: "1rem",
                  fontWeight: "bold",
                  fontSize: "20px",
                  letterSpacing: "3%",
                }}
              >
                Ordenar por:
              </div>
            )}
            <div className="sort-buttons">
              <Button
                className={sortBy === "featured" ? "active" : ""}
                onClick={() => handleSortClick("featured")}
              >
                Destacados
                {sortBy === "featured" && (
                  <TriangleIcon
                    direction={sortOrder === "asc" ? "up" : "down"}
                    className="ms-2"
                    color={sortBy === "featured" ? "white" : "#997404"}
                  />
                )}
              </Button>

              <Button
                className={sortBy === "price" ? "active" : ""}
                onClick={() => handleSortClick("price")}
              >
                Precio
                {sortBy === "price" && (
                  <TriangleIcon
                    direction={sortOrder === "asc" ? "up" : "down"}
                    className="ms-2"
                    color={sortBy === "price" ? "white" : "#997404"}
                  />
                )}
              </Button>
            </div>
          </div>
        </div>

        <Row>
          {(isMobile || isTablet) && showFilters && (
            <>
              <div className={`filter-menu ${showFilters ? "active" : ""}`}>
                <h3 className="filter-header">Filtros</h3>
                <div className="filter-content">
                  <div className="filter-section">
                    <h3>Categorías</h3>
                    <div className="category-list">
                      {categories.map((category) => (
                        <div key={category.id} className="category-item">
                          <label htmlFor={`category-${category.id}`}>
                            {category.name}
                          </label>
                          <input
                            type="checkbox"
                            id={`category-${category.id}`}
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => handleCategoryChange(category.id)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="filter-section">
                    <h3>
                      Tallas
                      {isFilteringSizes && (
                        <span style={{ fontSize: "0.8rem", color: "#666", marginLeft: "0.5rem" }}>
                          (Filtrando...)
                        </span>
                      )}
                    </h3>
                    {selectedCategories.length > 0 ? (
                      <div className="category-list">
                        {sizes.length > 0 ? (
                          sizes.map((size) => (
                            <div key={size.id} className="category-item">
                              <label htmlFor={`size-${size.id}`}>{size.description}</label>
                              <input
                                type="checkbox"
                                id={`size-${size.id}`}
                                checked={selectedSizes.includes(size.id)}
                                onChange={() => handleSizeChange(size.id)}
                              />
                            </div>
                          ))
                        ) : (
                          <div style={{ 
                            textAlign: "center", 
                            color: "#666", 
                            fontStyle: "italic",
                            padding: "1rem 0"
                          }}>
                            No hay tallas disponibles para el filtro seleccionado
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: "center", 
                        color: "#666", 
                        fontStyle: "italic",
                        padding: "1rem 0"
                      }}>
                        Coloca una categoría para ver las tallas disponibles
                      </div>
                    )}
                  </div>
                  <div className="filter-section">
                    <h3> Precio</h3>
                    <div className="price-range-container">
                      <Slider
                        value={priceRange}
                        onChange={handlePriceRangeChange}
                        min={0}
                        max={5000}
                        className="price-slider"
                      />
                      <div className="price-inputs">
                        <input
                          type="text"
                          value={"$" + inputPriceRange[0]}
                          onChange={(e) => handleInputChange(0, e.target.value)}
                          className="price-input"
                        />
                        <div className="price-separator" />
                        <input
                          type="text"
                          value={"$" + inputPriceRange[1]}
                          onChange={(e) => handleInputChange(1, e.target.value)}
                          className="price-input"
                        />
                      </div>
                      <div className="price-buttons">
                        <Button
                          className="apply-price-btn"
                          onClick={() => {
                            handleApplyPrice();
                            if (isMobile || isTablet) {
                              setShowFilters(false);
                            }
                          }}
                        >
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`filter-overlay ${showFilters ? "active" : ""}`}
                onClick={() => setShowFilters(false)}
              />
            </>
          )}

          {!(isMobile || isTablet) && (
            <Col lg={3} className="filters-column">
              <h3 className="filter-header">Filtros</h3>
              {/* Filters */}
              <div className="filter-section">
                <h3>Categorías</h3>
                <div className="category-list">
                  {categories.map((category) => (
                    <div key={category.id} className="category-item">
                      <label htmlFor={`category-${category.id}`}>
                        {category.name}
                      </label>
                      <input
                        type="checkbox"
                        id={`category-${category.id}`}
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleCategoryChange(category.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="filter-section">
                <h3>
                  Tallas
                  {isFilteringSizes && (
                    <span style={{ fontSize: "0.8rem", color: "#666", marginLeft: "0.5rem" }}>
                      (Filtrando...)
                    </span>
                  )}
                </h3>
                {selectedCategories.length > 0 ? (
                  <div className="category-list">
                    {sizes.length > 0 ? (
                      sizes.map((size) => (
                        <div key={size.id} className="category-item">
                          <label htmlFor={`size-${size.id}`}>{size.description}</label>
                          <input
                            type="checkbox"
                            id={`size-${size.id}`}
                            checked={selectedSizes.includes(size.id)}
                            onChange={() => handleSizeChange(size.id)}
                          />
                        </div>
                      ))
                    ) : (
                      <div style={{ 
                        textAlign: "center", 
                        color: "#666", 
                        fontStyle: "italic",
                        padding: "1rem 0"
                      }}>
                        No hay tallas disponibles para las categorías seleccionadas
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: "center", 
                    color: "#666", 
                    fontStyle: "italic",
                    padding: "1rem 0"
                  }}>
                    Selecciona una categoría para ver las tallas disponibles
                  </div>
                )}
              </div>
              <div className="filter-section">
                <h3> Precio</h3>
                <div className="price-range-container">
                  <Slider
                    value={priceRange}
                    onChange={handlePriceRangeChange}
                    min={0}
                    max={5000}
                    className="price-slider"
                  />
                  <div className="price-inputs">
                    <input
                      type="text"
                      value={"$" + inputPriceRange[0]}
                      onChange={(e) => handleInputChange(0, e.target.value)}
                      className="price-input"
                    />
                    <div className="price-separator" />
                    <input
                      type="text"
                      value={"$" + inputPriceRange[1]}
                      onChange={(e) => handleInputChange(1, e.target.value)}
                      className="price-input"
                    />
                  </div>
                  <div className="price-buttons">
                    <Button
                      className="apply-price-btn"
                      onClick={handleApplyPrice}
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              </div>
            </Col>
          )}
          {products.length === 0 ? (
            <Col lg={9} className="text-center">
              {isLoading ? (
                <div className={`no-results-container ${isMobile ? 'mobile' : ''}`}>
                  <h2 className="no-results-message">
                    Cargando productos...
                  </h2>
                  <div className="loading-spinner" style={{ 
                    width: "40px", 
                    height: "40px", 
                    border: "4px solid #f3f3f3", 
                    borderTop: "4px solid #997404", 
                    borderRadius: "50%", 
                    animation: "spin 1s linear infinite",
                    margin: "20px auto"
                  }}></div>
                </div>
              ) : (
                <div className={`no-results-container ${isMobile ? 'mobile' : ''}`}>
                  <h2 className="no-results-message">
                    No se encontraron resultados
                  </h2>
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                </div>
              )}
            </Col>
          ) : (
            <Col lg={9}>
              <div className="products-grid">
                {products.map((product) => (
                  <div key={`search product card ${product.id}`}>
                    <SearchProductCard
                      handleToast={handleToast}
                      product={product}
                      handleProductClick={handleProductClick}
                    />
                  </div>
                ))}
              </div>
            </Col>
          )}
        </Row>
        <Footer />
        {selectedProduct && (
          <ProductModal
            show={showModal}
            onHide={() => setShowModal(false)}
            productId={selectedProduct?.id}
          />
        )}
        <CustomToast
          onClose={() => {
            setShowToast({ message: "", show: false, success: true });
          }}
          message={showToast.message}
          show={showToast.show}
          success={showToast.success}
        />
      </div>
      )}
    </>
  );
};

export default SearchResults;
